import classNames from 'classnames'
import { DataModel } from 'legacy/lut/classes/models/DataModel'
import { IFormProfile } from 'legacy/lut/classes/models/IFormProfile'
import { IFormProfileExt } from 'legacy/lut/classes/models/IFormProfileExt'
import { MultipleContactWarning } from 'legacy/lut/components/common/multipleContactWarning/MultipleContactWarning'
import { ContactData } from 'legacy/lut/components/widgets/public/ContactData'
import { TabButtons } from 'legacy/lut/library/tabButtons'
import { IState } from 'legacy/lut/store'
import { ILookupState } from 'legacy/lut/store/state'
import { FC, useState } from 'react'
import { useSelector } from 'react-redux'

import { Translation } from 'cng-common/features/translations/components/translation'
import { Loader } from 'core-components/library/loader'
import { selectUser } from 'core-components/sitebuilder/store/selectors/selectUser'
import { IDataProviderSort } from 'core-components/types'
import { CheckAccessHelper } from 'core/classes/helpers/CheckAccessHelper'

import { WidgetAddContact } from '../AddContact'

import { KEY_CONTACT_ROLES, MNGR_ROLES } from './configs'
import { removeQueries } from './helpers/removeQueries'
import { ISelectBusinessProfileStateResult, IWidgetBusinessProfile } from './models'
import { selectBusinessProfileState } from './selectors/selectBusinessProfileState'

import styles from './BusinessProfile.module.scss'

// todo: replace in utils if doesn't exist there already
const delay = (ms: number) => {
  return new Promise((resolve) => setTimeout(resolve, ms))
}

const NEW_CONTACTS_ADDITIONAL_SORT: IDataProviderSort[] = [
  {
    field: 'FirstName',
    dir: 'asc',
  },
  {
    field: 'Created',
    dir: 'desc',
  },
]

export const WidgetBusinessProfile: FC<IWidgetBusinessProfile> = ({
  appSource,
  className,
  crudKey,
  disableEditAction,
  flexWidth,
  hideHeader,
  isOnboarding,
  pid,
  selectProfile,
  widgetParams,
  metrics,
}) => {
  const { selectedPartner, countries } = useSelector<
    IState & ILookupState,
    ISelectBusinessProfileStateResult
  >(selectBusinessProfileState)
  const [loader, setLoader] = useState(false)
  const [isKeyContactMissed, setIsKeyContactMissed] = useState(false)
  const [missedRoles, setMissedRoles] = useState<string[]>([])
  const { roleIds } = useSelector(selectUser)
  const [contactSaved, setContactSaved] = useState<IFormProfile | IFormProfileExt | null>(null)

  const onUpdateDataHandler = (data: DataModel[]) => {
    const needToCheck = CheckAccessHelper(roleIds, MNGR_ROLES)

    if (needToCheck && data && data.length > 0) {
      setMissedRoles([])

      const roles = new Set(
        data.reduce<string[]>((acc, curr) => {
          const prHpRole = curr.rawData.PR_HP_ROLE || ''
          const prHpRoleExp = curr.rawData.PR_HP_ROLEExt || ''
          const newRoles = [...prHpRole.split(';'), ...prHpRoleExp.split(';')].filter(Boolean)
          return [...acc, ...newRoles]
        }, [])
      )

      KEY_CONTACT_ROLES.forEach((keyRole) => {
        if (roles.has(keyRole)) {
          return
        }

        setMissedRoles((oldState) => [...oldState, keyRole])
      })

      setIsKeyContactMissed(missedRoles.length > 0)
    }
  }

  const onAddContactApply = (crudKey?: string) => () => {
    setLoader(true)
    if (!crudKey) {
      setLoader(false)
      return
    }
    removeQueries(crudKey)
    // Temp solution
    delay(100).then(() => {
      setLoader(false)
    })
  }

  return (
    <div className={classNames(styles.root, className)}>
      {isKeyContactMissed && (
        <div className={styles.warning_message}>
          <Translation span text="BUSINESS_PROFILE_MISSING_KEY_CONTARCT" />
          {`: ${missedRoles}`}
        </div>
      )}
      {hideHeader ? (
        <ContactData
          metrics={metrics}
          crudKey={crudKey}
          flexWidth={flexWidth}
          countries={countries}
          pid={pid}
          widgetParams={widgetParams}
          selectProfile={selectProfile}
          hideHeader
          appSource={appSource}
          disableEditAction={disableEditAction}
          onDataLoad={onUpdateDataHandler}
        />
      ) : (
        <TabButtons
          urlKey={''}
          defaultValue={'list'}
          className={'cmp-BusinessProfile-TabButtons'}
          items={[
            {
              id: 'list',
              label: 'List',
              content: () => {
                return (
                  <ContactData
                    metrics={metrics}
                    crudKey={crudKey}
                    flexWidth={flexWidth}
                    countries={countries}
                    pid={selectedPartner || pid}
                    widgetParams={widgetParams}
                    selectProfile={selectProfile}
                    appSource={appSource}
                    disableEditAction={disableEditAction}
                    onDataLoad={onUpdateDataHandler}
                    resetCrudQueries
                  />
                )
              },
            },
            {
              id: 'requests',
              label: 'Profiling Team Requests',
              content: () => {
                return (
                  <ContactData
                    metrics={metrics}
                    crudKey={crudKey}
                    flexWidth={flexWidth}
                    countries={countries}
                    pid={selectedPartner || pid}
                    onlyOpened
                    widgetParams={widgetParams}
                    selectProfile={selectProfile}
                    appSource={appSource}
                    disableEditAction={disableEditAction}
                    onDataLoad={onUpdateDataHandler}
                  />
                )
              },
            },
            {
              id: 'newcontacts',
              title: () => (
                <span className={styles.tabButtonTitle}>
                  New Contacts
                  <MultipleContactWarning />
                </span>
              ),
              content: () => {
                return (
                  <>
                    <WidgetAddContact
                      isOnboarding={isOnboarding}
                      PartnerID={pid || widgetParams.PartnerProId}
                      appSource={appSource}
                      onApply={onAddContactApply(crudKey)}
                      setContactSaved={setContactSaved}
                    />
                    {!loader ? (
                      <ContactData
                        metrics={metrics}
                        crudKey={crudKey}
                        flexWidth={flexWidth}
                        countries={countries}
                        pid={selectedPartner || pid || widgetParams.PartnerProId}
                        defaultShowNew
                        widgetParams={widgetParams}
                        selectProfile={selectProfile}
                        appSource={appSource}
                        disableEditAction={disableEditAction}
                        onDataLoad={onUpdateDataHandler}
                        contactSaved={contactSaved}
                        additionalSort={NEW_CONTACTS_ADDITIONAL_SORT}
                        resetCrudQueries
                      />
                    ) : (
                      <Loader />
                    )}
                  </>
                )
              },
            },
          ]}
        />
      )}
    </div>
  )
}
